import {
  Button,
  Card,
  CardActions,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { withRemoteDataAndSpinner } from "hoc/withRemoteDataAndSpinner";
import withRouter from "hoc/withRouter";
import { useLoadAll } from "hooks/useLoadAll";
import { Link, useNavigate } from "react-router-dom";
import customersService from "services/admin/customersService";
import _ from "lodash";
import { expiredInDays, toDate, toDocumentFormat, toFullDateName } from "helpers/dateHelper";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import moment from "moment";
import CustomerOrders from "components/CustomerOrders/CustomerOrders";
import CustomerTickets from "components/CustomerTickets/CustomerTickets";
import { snack } from "components/Snack/Snack";
import type { Customer } from "interfaces/entities/customer.model";
import { PlatformIcon } from "components/PlatformIcon/PlatformIcon";
import { FaUnlink } from "react-icons/fa";
import { toast } from "react-toastify";

const gender = {
  F: "Женщина",
  M: "Мужчина",
};

interface CustomerProps {
  data: {
    customer: Customer;
  };
}
function CustomerView({ data: { customer } }: CustomerProps) {
  const navigate = useNavigate();
  const onDelete = async (item: Customer) => {
    try {
      const isConfirmed = window.confirm("Вы точно хотите удалить клиента?");

      if (isConfirmed) {
        await customersService.delete(item.id);
        snack("Клиент успешно удален!");
        navigate("/admin/customers");
      }
    } catch (ex: any) {
      console.error("При удаление клиента вышла ошибка:", ex?.message);
      snack("При удаление клиента вышла ошибка:" + ex?.message);
    }
  };

  const deleteContactFromCustomer = async (contactID: number) => {
    try {
      const isConfirmed = window.confirm("Вы точно хотите отвязать контакт?");
      if (isConfirmed) {
        await customersService.deleteContactFromCustomer(customer.id, contactID);
        toast.success("Контакт успешно отвязан!");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (ex: any) {
      toast.error("При отвязке контакта вышла ошибка:" + ex?.message);
    }
  };

  const passport = _.get(customer, "passport");
  const IDCard = _.get(customer, "IDCard");

  return (
    <div>
      <Card>
        <CardContent>
          <div className="flex gap-8">
            <div>
              <img
                style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "50%" }}
                src={customer.photo || `/media/images/avatar_m.jpg`}
              />
            </div>
            <div className="profile w-100">
              <div className="flex gap-8 flex-between">
                <h3>
                  {_.get(customer, "lastName")} {_.get(customer, "firstName")}
                </h3>
                <div>
                  <span className="fs-12">UserID: {customer.userID}</span>
                </div>
              </div>
              {customer.user?.phone && (
                <div className="m-t-16 color-blue">{customer.user.phone}</div>
              )}
              <div className="m-t-8">{customer.user.email}</div>
              <div className="m-t-8">Номер телефона: {customer.user.phone}</div>
              <div className="m-t-8">
                Дата рождения:{" "}
                {customer.birthdate
                  ? moment(customer.birthdate).format("DD.MM.YYYY")
                  : "Отсутсвует"}
              </div>
              <div className="m-t-8">
                Пол:{" "}
                {customer.gender ? gender[customer.gender as keyof typeof gender] : customer.gender}
              </div>
              <div className="m-t-8">
                Гражданство: {_.get(customer, "citizenship.name", "Отсутсвует")}
              </div>
              <div className="m-t-8">Является клиентом с {toFullDateName(customer.createdAt)}</div>
              <div className="m-t-16 fs-14">Заметка: {_.get(customer, "comment", "")}</div>
            </div>
          </div>
        </CardContent>
        <CardActions className="justify-end">
          <Link to={`/admin/customers/edit/${customer.id}`}>
            <Button size="small">Редактировать</Button>
          </Link>
          <Link to="#" onClick={() => onDelete(customer)}>
            <Button size="small">Удалить</Button>
          </Link>
        </CardActions>
      </Card>
      {/* Свзяанные контакты */}
      <Card className="m-t-32">
        <CardContent>
          <h3 className="m-b-16">Связанные контакты</h3>
          <div className="m-t-8">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Аккаунт</TableCell>
                    <TableCell>Платформа</TableCell>
                    <TableCell>Статус подписки</TableCell>
                    <TableCell>Дата добавления</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customer.contacts?.map((contact) => {
                    return (
                      <TableRow key={contact.id}>
                        <TableCell>
                          {contact.externalUserUserName ||
                            contact.externalUserFirstName ||
                            contact.externalUserID}
                        </TableCell>
                        <TableCell>
                          <PlatformIcon platform={contact.platform} />
                        </TableCell>
                        <TableCell>
                          {contact.isUserFollowBusiness ? "Подписан" : "Не подписан"}
                        </TableCell>
                        <TableCell>{toDate(contact.createdAt)}</TableCell>
                        <TableCell>
                          <Tooltip title="Отвязать контакт">
                            <IconButton
                              color="info"
                              onClick={() => deleteContactFromCustomer(customer?.id)}
                            >
                              <FaUnlink size={16} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </CardContent>
      </Card>

      <CustomerTickets customerID={customer.id} />

      <Card className="m-t-32">
        <CardContent>
          <h3 className="m-b-16">Данные о удостоверение личности</h3>
          <div className="m-t-8">
            <span>Номер: </span>
            <span>{IDCard?.iin || "Отсутсвует"}</span>
          </div>
          <div className="m-t-8">
            <div>Фото версия документа:</div>
            <div>
              {IDCard ? (
                <div className="m-t-8" style={{ marginLeft: "" }}>
                  <PhotoProvider>
                    {IDCard.photoFront ? (
                      <PhotoView src={IDCard.photoFront}>
                        <div style={{ width: 200 }}>
                          <img src={IDCard.photoFront} alt="Photo Front" />
                        </div>
                      </PhotoView>
                    ) : (
                      <div>Фронтальная часть: Отсутсвует</div>
                    )}
                    {IDCard.photoBack ? (
                      <PhotoView src={IDCard.photoBack}>
                        <div style={{ width: 200 }}>
                          <img src={IDCard.photoBack} alt="Photo Back" />
                        </div>
                      </PhotoView>
                    ) : (
                      <div>Обратная сторона: Отсутсвует</div>
                    )}
                  </PhotoProvider>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="m-t-32">
        <CardContent>
          <h3 className="m-b-16">Данные о паспорте</h3>
          <div className="m-t-8">
            <span>Номер пасспорта: </span>
            <span>{passport?.number || "Отсутсвует"}</span>
          </div>
          <div className="m-t-8">
            <span>Дата выдачи паспорта: </span>
            <span>
              {passport?.dateOfIssue ? toDocumentFormat(passport?.dateOfIssue) : "Отсутсвует"}
            </span>
          </div>
          <div className="m-t-8">
            <span>Срок истечения паспорта: </span>
            <span>
              {passport?.dateOfExpire ? toDocumentFormat(passport?.dateOfExpire) : "Отсутсвует"}
            </span>
          </div>
          <div className="m-t-8">
            <span>Фото версия документа:</span>
            <span>
              {passport?.photo ? (
                <div className="m-t-16" style={{ marginLeft: "-8px" }}>
                  <PhotoProvider>
                    <PhotoView src={passport.photo}>
                      <div style={{ width: 200 }}>
                        <img src={passport.photo} alt="Photo of passport" />
                      </div>
                    </PhotoView>
                  </PhotoProvider>
                </div>
              ) : (
                <span> Отсутсвует</span>
              )}
            </span>
          </div>
        </CardContent>
      </Card>
      {/* <Card className="m-t-32">
        <CardContent>
          <h3 className="m-b-16">История получения виз</h3>
          {customer.visas && customer.visas.length > 0 ? (
            <div>
              {customer.visas.map((v: any) => {
                return (
                  <div>
                    <strong>Годен до: </strong>
                    <span>{v.expiredAt ? toDocumentFormat(v.expiredAt) : "Отсутсвует"}</span>
                    &nbsp; {expiredInDays(v.expiredAt)}
                  </div>
                );
              })}
            </div>
          ) : (
            "Данные отсутсвуют"
          )}
        </CardContent>
      </Card> */}

      {/* <CustomerOrders customerID={customer.id}></CustomerOrders> */}
    </div>
  );
}
export default withRouter(
  withRemoteDataAndSpinner(CustomerView, (router: any) => {
    return useLoadAll({
      customer: () => customersService.get(router.params.id),
    });
  }),
);
